import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import {
  AddCommentDocument,
  CommentEdge,
  GetVideoCommentsDocument,
  RemoveCommentDocument,
} from '~/__generated__/graphql'
import { CacheEdges } from '~/libs/apollo/cursorConnection'
import * as GA from '~/libs/ga'
import { snackbarTextState } from '~/recoil/snackbar'
import { Configs } from '../configs'

export const useCommentsMutation = (videoId: string) => {
  const { t } = useTranslation()
  const setSnackbarText = useSetRecoilState(snackbarTextState)
  const [addComment] = useMutation(AddCommentDocument)
  const [removeComment] = useMutation(RemoveCommentDocument)

  const createComment = useCallback(
    async (contents: string) => {
      GA.sendEvent({
        event: 'review',
        screen: 'movie_page',
        category: 'button',
        action: 'movie_page_review',
        label: videoId,
      })

      try {
        await addComment({
          variables: { videoId: videoId, contents },
          refetchQueries: [
            // 動画のコメントを再読み込み
            {
              query: GetVideoCommentsDocument,
              variables: { id: videoId, first: Configs.comments.limit },
            },
          ],
        })
        setSnackbarText(t('sent'))
      } catch (err) {
        console.error(err)
        setSnackbarText(t('error_occurred'))
      }
    },
    [videoId, t, addComment, setSnackbarText],
  )

  const deleteComment = useCallback(
    async (commentId: string) => {
      try {
        await removeComment({
          variables: { id: commentId },
          update: (cache, result, options) => {
            // 削除したコメントをキャッシュから消す
            const comment = result.data?.removeComment?.comment
            if (!!comment) {
              cache.modify({
                id: `VideoCommentsConnection:${videoId}`,
                fields: {
                  edges(value) {
                    if (!!value) {
                      const existing = value as CacheEdges<CommentEdge>
                      const nodes = Object.assign({}, existing.nodes)
                      const cursors = Object.assign([], existing.cursors)
                      const ids = Object.assign([], existing.ids)
                      const index = ids.indexOf(comment.id)
                      if (index > -1) {
                        const removeCursors = cursors.splice(index, 1)
                        ids.splice(index, 1)
                        delete nodes[removeCursors[0]]
                        cache.evict({ id: cache.identify(comment) })
                        cache.gc()
                        return { nodes, cursors, ids }
                      }
                    }
                    return value
                  },
                },
              })
            }
          },
        })
        setSnackbarText(t('deleted'))
      } catch (err) {
        console.error(err)
        setSnackbarText(t('error_occurred'))
      }
    },
    [removeComment, setSnackbarText, t, videoId],
  )

  return {
    createComment,
    deleteComment,
  }
}
